import React, { createContext, useContext, useState, useRef, useCallback } from 'react';

const PlayerContext = createContext();

export const usePlayer = () => {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error('usePlayer must be used within PlayerProvider');
  }
  return context;
};

export const PlayerProvider = ({ children, roomId }) => {
  const [myPlayerId, setMyPlayerId] = useState(null);
  const [isObserver, setIsObserver] = useState(false);
  const [observers, setObservers] = useState([]);
  const [players, setPlayers] = useState(new Map());
  const [playerSpawnCorners, setPlayerSpawnCorners] = useState(new Map());
  
  // Refs for maintaining identity across reconnections
  const myPlayerIdRef = useRef(null);
  const myUniquePlayerIdRef = useRef(null);

  // Player ID storage functions
  const getStoredPlayerId = useCallback(() => {
    const currentRoom = roomId || window.location.pathname.split('/').pop();
    return sessionStorage.getItem(`bomberman-playerId-${currentRoom}`);
  }, [roomId]);

  const setStoredPlayerId = useCallback((id) => {
    const currentRoom = roomId || window.location.pathname.split('/').pop();
    if (currentRoom && id) {
      sessionStorage.setItem(`bomberman-playerId-${currentRoom}`, id);
    }
    myPlayerIdRef.current = id;
    setMyPlayerId(id);
  }, [roomId]);

  // Unique player ID storage for reconnection
  const getStoredUniquePlayerId = useCallback(() => {
    const currentRoom = roomId || window.location.pathname.split('/').pop();
    return sessionStorage.getItem(`bomberman-uniquePlayerId-${currentRoom}`);
  }, [roomId]);

  const setStoredUniquePlayerId = useCallback((uniquePlayerId, serverRoomId) => {
    const currentRoom = serverRoomId || roomId || window.location.pathname.split('/').pop();
    if (currentRoom && uniquePlayerId) {
      sessionStorage.setItem(`bomberman-uniquePlayerId-${currentRoom}`, uniquePlayerId);
    }
    myUniquePlayerIdRef.current = uniquePlayerId;
  }, [roomId]);

  // Memoized computed values
  const myPlayer = React.useMemo(() => {
    return myPlayerId !== null && myPlayerId !== undefined ? players.get(myPlayerId) : null;
  }, [myPlayerId, players]);

  // Player management actions
  const actions = React.useMemo(() => ({
    setMyPlayerId: (id) => {
      myPlayerIdRef.current = id;
      setMyPlayerId(id);
    },
    setIsObserver,
    setObservers,
    setPlayers,
    setPlayerSpawnCorners,
    
    // Storage actions
    getStoredPlayerId,
    setStoredPlayerId,
    getStoredUniquePlayerId,
    setStoredUniquePlayerId,

    // Player utilities
    updatePlayer: (playerId, updates) => {
      setPlayers(prev => {
        const newPlayers = new Map(prev);
        const player = newPlayers.get(playerId);
        if (player) {
          newPlayers.set(playerId, { ...player, ...updates });
        }
        return newPlayers;
      });
    },

    addPlayer: (playerData) => {
      setPlayers(prev => new Map(prev.set(playerData.playerId, playerData)));
    },

    removePlayer: (playerId) => {
      setPlayers(prev => {
        const newPlayers = new Map(prev);
        newPlayers.delete(playerId);
        return newPlayers;
      });
    },

    addObserver: (observerData) => {
      setObservers(prev => [...prev, observerData]);
    },

    removeObserver: (observerId) => {
      setObservers(prev => prev.filter(obs => obs.id !== observerId));
    },

    resetPlayers: () => {
      setPlayers(new Map());
      setObservers([]);
      setPlayerSpawnCorners(new Map());
    }
  }), [getStoredPlayerId, setStoredPlayerId, getStoredUniquePlayerId, setStoredUniquePlayerId]);

  // Memoized state values
  const state = React.useMemo(() => ({
    myPlayerId,
    myPlayer,
    isObserver,
    observers,
    players,
    playerSpawnCorners,
    myPlayerIdRef,
    myUniquePlayerIdRef
  }), [myPlayerId, myPlayer, isObserver, observers, players, playerSpawnCorners]);

  const value = React.useMemo(() => ({
    ...state,
    ...actions
  }), [state, actions]);

  return (
    <PlayerContext.Provider value={value}>
      {children}
    </PlayerContext.Provider>
  );
};

export default PlayerContext;